import {booleanAttribute, Component, inject, Input, OnInit} from '@angular/core';
import {KENDO_BUTTONS} from '@progress/kendo-angular-buttons';
import {KENDO_INPUTS} from '@progress/kendo-angular-inputs';

import {ValLangueService} from '../../../../../val-definitions-communes-lib/src/public-api';
import {ValAbonnementNotification, ValAbonnementsNotificationService, ValCanauxNotification, ValTypeEvenementNotification} from '../../../../../val-ammon-services/val-services-communs-lib/src/public-api';

import {FonteIconeComponent} from '../fonte-icone/fonte-icone.component';
import {PanneauInformationComponent} from '../panneau-information/panneau-information.component';
import {SimpleBoutonComponent} from '../simple-bouton/simple-bouton.component';

//Représente une ligne de l’écran : un type d’événement et ses abonnements par canal
export class LigneMesAbonnements
{
  //Création
  //  Paramètres
  //    type : Type d’événement
  constructor (type: ValTypeEvenementNotification)
  {
    this.type = type;
    this.abonnementEmail = null;
    this.abonnementTeams = null;
    this.urlTeams = "";
    this.remplacementUrlTeams = false;
    this.operationEnCours = false;
  }

  //Type d’événement
  public type: ValTypeEvenementNotification;

  //Abonnement e-mail, null si l’utilisateur n’est pas abonné
  public abonnementEmail: ValAbonnementNotification | null;

  //Abonnement Teams, null si l’utilisateur n’est pas abonné
  public abonnementTeams: ValAbonnementNotification | null;

  //URL de workflow Teams saisie : jamais celle enregistrée, que l’API ne rend pas
  public urlTeams: string;

  //Indique que l’utilisateur remplace une URL Teams déjà configurée
  public remplacementUrlTeams: boolean;

  //Indique qu’un appel est en cours sur la ligne
  public operationEnCours: boolean;

  //Indique si une URL Teams est enregistrée
  public get TeamsConfigure () : boolean
  {
    return !!this.abonnementTeams && this.abonnementTeams.destinationConfiguree !== false;
  }

  //Indique si la saisie de l’URL Teams est affichée
  public get SaisieUrlTeamsVisible () : boolean
  {
    return !this.TeamsConfigure || this.remplacementUrlTeams;
  }
}

//Message affiché à l’utilisateur après une action
export class MessageMesAbonnements
{
  //Création
  //  Paramètres
  //    texte  : Texte
  //    erreur : Indique s’il s’agit d’une erreur
  constructor (texte: string, erreur: boolean)
  {
    this.texte = texte;
    this.erreur = erreur;
  }

  //Texte
  public texte: string;

  //Indique s’il s’agit d’une erreur
  public erreur: boolean;
}

//Composant « Mes abonnements » aux notifications (API Ammon Notification).
//Liste les types d’événement autorisés pour l’utilisateur ; pour chacun, abonnement par e-mail à l’adresse
//du compte (non modifiable) et par Teams via une URL de workflow, qui n’est jamais réaffichée une fois enregistrée.
//  Utilisation : <val-mes-abonnements></val-mes-abonnements>, chargement à l’affichage
@Component (
{
  selector: 'val-mes-abonnements',
  imports:
  [
    KENDO_BUTTONS,
    KENDO_INPUTS,
    FonteIconeComponent,
    PanneauInformationComponent,
    SimpleBoutonComponent
  ],
  templateUrl: './mes-abonnements.component.html',
  styleUrl: './mes-abonnements.component.scss'
})
export class MesAbonnementsComponent implements OnInit
{
  private abonnementsService = inject (ValAbonnementsNotificationService);
  private valLangueService = inject (ValLangueService);

  //Création
  constructor ()
  {
    this.chargementAutomatique = true;
    this.chargementEnCours = false;
    this.erreurChargement = false;
    this.adresseEmail = null;
    this.lignes = [];
    this.message = null;
  }

  //Indique si les abonnements sont chargés dès l’affichage du composant
  @Input ({transform: booleanAttribute})
  public chargementAutomatique: boolean;

  //Indique que le chargement est en cours
  public chargementEnCours: boolean;

  //Indique que le chargement a échoué
  public erreurChargement: boolean;

  //Adresse e-mail du compte, null si le compte n’en a pas
  public adresseEmail: string | null;

  //Lignes de l’écran, une par type d’événement autorisé
  public lignes: LigneMesAbonnements[];

  //Dernier message de succès ou d’erreur
  public message: MessageMesAbonnements | null;

  //Indique si l’API Notification est configurée
  public get Configuree () : boolean
  {
    return this.abonnementsService.Configuree;
  }

  //Indique si l’abonnement par e-mail est possible : le compte doit porter une adresse
  public get EmailDisponible () : boolean
  {
    return !!this.adresseEmail;
  }

  //Initialisation
  public ngOnInit () : void
  {
    if (this.chargementAutomatique)
      void this.Charger ();
  }

  //Traduit un texte de l’écran
  //  Paramètres
  //    cle    : Clé, sous valcomposantslib.mes-abonnements
  //    defaut : Texte français, affiché si l’application ne porte pas la clé
  public Texte (cle: string, defaut: string) : string
  {
    return this.valLangueService.TraduireOuDefaut (`valcomposantslib.mes-abonnements.${cle}`, defaut);
  }

  //Libellé d’un type d’événement : celui de l’API, traduit hors langue par défaut si la clé existe
  //  Paramètres
  //    type : Type d’événement
  public LibelleType (type: ValTypeEvenementNotification) : string
  {
    return this.valLangueService.TraduireLibelleBase (`valcomposantslib.mes-abonnements.types.${type.code}`, type.libelle || type.code);
  }

  //Charge les types d’événement et les abonnements de l’utilisateur
  public async Charger () : Promise<void>
  {
    this.chargementEnCours = true;
    this.erreurChargement = false;

    try
    {
      if (!this.Configuree)
      {
        this.lignes = [];
        this.erreurChargement = true;
        return;
      }

      const donnees = await this.abonnementsService.RetournerAbonnements ();

      this.adresseEmail = donnees.adresseEmail?.trim () || null;
      this.lignes = donnees.typesEvenement.map (type =>
      {
        const ligne: LigneMesAbonnements = new LigneMesAbonnements (type);

        ligne.abonnementEmail = donnees.abonnements.find (abonnement => abonnement.codeTypeEvenement === type.code && abonnement.canal === ValCanauxNotification.Email) ?? null;
        ligne.abonnementTeams = donnees.abonnements.find (abonnement => abonnement.codeTypeEvenement === type.code && abonnement.canal === ValCanauxNotification.Teams) ?? null;

        return ligne;
      });
    }
    catch
    {
      this.lignes = [];
      this.erreurChargement = true;
    }
    finally
    {
      this.chargementEnCours = false;
    }
  }

  //Abonne ou désabonne par e-mail
  //  Paramètres
  //    ligne : Ligne du type d’événement
  //    coche : Indique si la case est cochée
  public async ChangerEmail (ligne: LigneMesAbonnements, coche: boolean) : Promise<void>
  {
    if (!this.EmailDisponible || coche === !!ligne.abonnementEmail)
      return;

    await this.Executer (ligne, async () =>
    {
      if (coche)
      {
        ligne.abonnementEmail = await this.abonnementsService.Abonner (ligne.type.code, ValCanauxNotification.Email, null);
        this.Afficher (this.Texte ("succes.abonnement-email", "Abonnement par e-mail enregistré."), false);
      }
      else
      {
        await this.abonnementsService.Desabonner (ligne.abonnementEmail!.id);
        ligne.abonnementEmail = null;
        this.Afficher (this.Texte ("succes.desabonnement-email", "Désabonnement par e-mail enregistré."), false);
      }
    });
  }

  //Envoie un message de test sur Teams : à l’URL saisie, à défaut à l’URL enregistrée
  //  Paramètres
  //    ligne : Ligne du type d’événement
  public async TesterTeams (ligne: LigneMesAbonnements) : Promise<void>
  {
    const url: string = ligne.urlTeams.trim ();

    if (!url && !ligne.TeamsConfigure)
      return;

    if (url && !this.UrlTeamsValide (url))
      return;

    await this.Executer (ligne, async () =>
    {
      const resultat = await this.abonnementsService.TesterCanal (ValCanauxNotification.Teams, url || null);

      if (resultat?.succes)
        this.Afficher (this.Texte ("succes.test-teams", "Message de test envoyé sur Teams."), false);
      else
        this.Afficher (resultat?.erreur?.trim () || this.Texte ("erreur.test-teams", "Le message de test n’a pas pu être envoyé."), true);
    });
  }

  //Enregistre l’URL Teams saisie : abonne, ou remplace l’URL déjà configurée
  //  Paramètres
  //    ligne : Ligne du type d’événement
  public async EnregistrerTeams (ligne: LigneMesAbonnements) : Promise<void>
  {
    const url: string = ligne.urlTeams.trim ();

    if (!this.UrlTeamsValide (url))
      return;

    await this.Executer (ligne, async () =>
    {
      ligne.abonnementTeams = await this.abonnementsService.Abonner (ligne.type.code, ValCanauxNotification.Teams, url);
      ligne.urlTeams = "";
      ligne.remplacementUrlTeams = false;
      this.Afficher (this.Texte ("succes.abonnement-teams", "Abonnement Teams enregistré."), false);
    });
  }

  //Affiche la saisie d’une nouvelle URL Teams à la place de « configurée »
  //  Paramètres
  //    ligne : Ligne du type d’événement
  public RemplacerTeams (ligne: LigneMesAbonnements) : void
  {
    ligne.urlTeams = "";
    ligne.remplacementUrlTeams = true;
  }

  //Abandonne le remplacement de l’URL Teams
  //  Paramètres
  //    ligne : Ligne du type d’événement
  public AnnulerRemplacementTeams (ligne: LigneMesAbonnements) : void
  {
    ligne.urlTeams = "";
    ligne.remplacementUrlTeams = false;
  }

  //Désabonne de Teams : l’URL enregistrée est supprimée
  //  Paramètres
  //    ligne : Ligne du type d’événement
  public async DesabonnerTeams (ligne: LigneMesAbonnements) : Promise<void>
  {
    if (!ligne.abonnementTeams)
      return;

    await this.Executer (ligne, async () =>
    {
      await this.abonnementsService.Desabonner (ligne.abonnementTeams!.id);
      ligne.abonnementTeams = null;
      ligne.urlTeams = "";
      ligne.remplacementUrlTeams = false;
      this.Afficher (this.Texte ("succes.desabonnement-teams", "Désabonnement Teams enregistré."), false);
    });
  }

  //Indique si une URL Teams est saisie
  //  Paramètres
  //    ligne : Ligne du type d’événement
  public UrlTeamsSaisie (ligne: LigneMesAbonnements) : boolean
  {
    return ligne.urlTeams.trim () !== "";
  }

  //Ferme le message affiché
  public FermerMessage () : void
  {
    this.message = null;
  }

  //Contrôle l’URL Teams avant envoi : HTTPS obligatoire. L’hôte est contrôlé par l’API
  //  Paramètres
  //    url : URL saisie
  //  Retourne
  //    Vrai si l’URL est recevable, sinon le message d’erreur est affiché
  private UrlTeamsValide (url: string) : boolean
  {
    let valide: boolean;

    try
    {
      valide = new URL (url).protocol === "https:";
    }
    catch
    {
      valide = false;
    }

    if (!valide)
      this.Afficher (this.Texte ("erreur.url-teams", "L’URL du workflow Teams doit être une adresse https:// valide."), true);

    return valide;
  }

  //Exécute un appel sur une ligne : une seule opération à la fois, message d’erreur en cas d’échec
  //  Paramètres
  //    ligne     : Ligne du type d’événement
  //    operation : Opération
  private async Executer (ligne: LigneMesAbonnements, operation: () => Promise<void>) : Promise<void>
  {
    if (ligne.operationEnCours)
      return;

    ligne.operationEnCours = true;
    this.message = null;

    try
    {
      await operation ();
    }
    catch (erreur)
    {
      this.Afficher (this.abonnementsService.MessageErreur (erreur) ?? this.Texte ("erreur.generique", "L’opération n’a pas pu aboutir. Réessayez plus tard."), true);
    }
    finally
    {
      ligne.operationEnCours = false;
    }
  }

  //Affiche un message
  //  Paramètres
  //    texte  : Texte
  //    erreur : Indique s’il s’agit d’une erreur
  private Afficher (texte: string, erreur: boolean) : void
  {
    this.message = new MessageMesAbonnements (texte, erreur);
  }
}
